import React, { useEffect, useState } from 'react';
import { findMatch } from '../services/matching';
import { joinCommunity } from '../services/communityMatching';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import VeylLogo from './VeylLogo';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-14px); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.08); }
  }
  @keyframes ripple {
    0% { transform: scale(0.85); opacity: 0.6; }
    100% { transform: scale(1.5); opacity: 0; }
  }
  .waiting-primary-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .waiting-primary-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.45) !important;
  }
  .waiting-secondary-btn {
    transition: border-color 0.2s, background-color 0.2s, color 0.2s !important;
  }
  .waiting-secondary-btn:hover:not(:disabled) {
    border-color: rgba(0,128,129,0.5) !important;
    background-color: rgba(0,128,129,0.08) !important;
    color: #008081 !important;
  }
`;

export default function WaitingPage({ userData, user, setUserData, setCurrentPage }) {
  const { t, language } = useLanguage();
  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [noMatchFound, setNoMatchFound] = useState(false);
  // The server's `unmatch` function stamps both veyls with `unmatchedBy`
  // (the person who ended the match). A veyl with that field has had a
  // previous partner, so this is a rematch; if it was the OTHER person who
  // ended it, we also tell this person why they're back here. findMatch
  // and respondToMatchRequest clear the field again on a new match.
  const [isRematch, setIsRematch] = useState(!!userData?.activeVeyl?.unmatchedBy);
  const [partnerEndedMatch, setPartnerEndedMatch] = useState(
    !!userData?.activeVeyl?.unmatchedBy && userData.activeVeyl.unmatchedBy !== user?.uid
  );
  const [isMatched, setIsMatched] = useState(false);
  const [joiningCommunity, setJoiningCommunity] = useState(false);
  const [dots, setDots] = useState('');
  const [goalCategory, setGoalCategory] = useState(userData?.goalCategory || userData?.activeVeyl?.goalCategory || null);

  const activeVeylId = userData?.activeVeylId;
  const alreadyInCommunity = !!userData?.communityId;

  // Load goal category from veyl if not in userData
  useEffect(() => {
    if (!goalCategory && activeVeylId) {
      getDoc(doc(db, 'veyls', activeVeylId))
        .then(snap => {
          if (snap.exists()) {
            setGoalCategory(snap.data().goalCategory || null);
          }
        })
        .catch(err => {
          console.error('Failed to load goal category from veyl:', err);
        });
    }
  }, [activeVeylId, goalCategory]);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Live listener — navigate to dashboard when matched
  useEffect(() => {
    if (!activeVeylId) return;
    const unsubscribe = onSnapshot(doc(db, 'veyls', activeVeylId), (snap) => {
      if (snap.exists()) {
        const veylData = snap.data();
        if (veylData.goalCategory) setGoalCategory(veylData.goalCategory);
        setIsRematch(!!veylData.unmatchedBy);
        setPartnerEndedMatch(!!veylData.unmatchedBy && veylData.unmatchedBy !== user?.uid);
        if (veylData.partnerId && veylData.status === 'matched') {
          setIsMatched(true);
          setUserData(prev => ({ ...prev, activeVeyl: veylData }));
          setCurrentPage('dashboard');
        }
      }
    });
    return () => unsubscribe();
  }, [activeVeylId]);

  // Polling — checks if already matched before trying to find a match
  useEffect(() => {
    if (!activeVeylId || !user || isMatched) return;

    const interval = setInterval(async () => {
      try {
        // First check if already matched — don't run findMatch if so
        const snap = await getDoc(doc(db, 'veyls', activeVeylId));
        if (snap.exists() && snap.data().status === 'matched') {
          setIsMatched(true);
          clearInterval(interval);
          return;
        }

        const found = await findMatch(
          user.uid,
          {
            goalCategory,
            blockedUsers: userData?.blockedUsers || [],
            ageGroup: userData?.ageGroup,
            timezone: userData?.timezone,
            matchPreference: userData?.matchPreference,
            name: userData?.name
          },
          activeVeylId
        );

        if (found) {
          setIsMatched(true);
          setNoMatchFound(false);
        } else {
          setNoMatchFound(true);
        }
      } catch (err) {
        console.error('findMatch polling error:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user?.uid, activeVeylId, goalCategory, isMatched]);

  const manualCheck = async () => {
    if (!activeVeylId || isMatched) return;
    setChecking(true);
    setNoMatchFound(false);

    try {
      // First check if already matched
      const snap = await getDoc(doc(db, 'veyls', activeVeylId));
      if (snap.exists() && snap.data().status === 'matched') {
        setIsMatched(true);
        setUserData(prev => ({ ...prev, activeVeyl: snap.data() }));
        setCurrentPage('dashboard');
        return;
      }

      const found = await findMatch(
        user.uid,
        {
          goalCategory,
          blockedUsers: userData?.blockedUsers || [],
          ageGroup: userData?.ageGroup,
          timezone: userData?.timezone,
          matchPreference: userData?.matchPreference,
          name: userData?.name
        },
        activeVeylId
      );

      setAttempts(prev => prev + 1);
      if (!found) setNoMatchFound(true);
    } catch (err) {
      console.error('Manual check error:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleJoinCommunity = async () => {
    if (!goalCategory) {
      alert('Could not determine your goal category. Please try again.');
      return;
    }
    setJoiningCommunity(true);
    try {
      const communityId = await joinCommunity(
        user.uid,
        goalCategory,
        activeVeylId,
        userData.name,
        userData.avatarId,
        language
      );
      if (communityId) {
        setUserData(prev => ({ ...prev, communityId }));
        setCurrentPage('dashboard');
      } else {
        alert(t('error'));
      }
    } catch (err) {
      alert(t('error') + ': ' + err.message);
    } finally {
      setJoiningCommunity(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#080f1a', padding: '1.5rem',
      position: 'relative', overflow: 'hidden'
    }}>
      <style>{styleTag}</style>

      {/* Background blobs */}
      <div style={{
        position: 'fixed', top: '-15%', left: '-10%',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(75,44,94,0.35) 0%, transparent 70%)',
        pointerEvents: 'none', animation: 'float 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'fixed', bottom: '-15%', right: '-10%',
        width: '600px', height: '600px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,128,129,0.25) 0%, transparent 70%)',
        pointerEvents: 'none', animation: 'float 10s ease-in-out infinite reverse'
      }} />

      {/* Logo */}
      <VeylLogo width={110} />

      {/* Main card */}
      <div style={{
        maxWidth: '28rem', width: '100%',
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        boxShadow: '0 25px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
        padding: '2.5rem 2rem',
        border: '1px solid rgba(255,255,255,0.08)',
        textAlign: 'center',
        animation: 'fadeInUp 0.5s ease',
        position: 'relative', zIndex: 1
      }}>

        {/* Animated search icon */}
        <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 2rem' }}>
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid rgba(0,128,129,0.3)',
            animation: 'ripple 2s ease-out infinite'
          }} />
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid rgba(0,128,129,0.2)',
            animation: 'ripple 2s ease-out infinite 0.6s'
          }} />
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid rgba(0,128,129,0.1)',
            animation: 'ripple 2s ease-out infinite 1.2s'
          }} />
          <div style={{
            position: 'absolute', inset: '12px',
            background: 'linear-gradient(135deg, rgba(0,128,129,0.2), rgba(75,44,94,0.2))',
            border: '1px solid rgba(0,128,129,0.3)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.75rem',
            animation: 'pulse 2.5s ease-in-out infinite'
          }}>
            {isRematch ? '🔄' : '🔍'}
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '1.5rem', fontWeight: '800', marginBottom: '0.75rem',
          color: '#ffffff', letterSpacing: '-0.02em'
        }}>
          {isRematch ? t('findingNewMatch') : t('findingMatch')}
        </h2>

        {/* Subtitle */}
        <p style={{
          color: 'rgba(255,255,255,0.45)', marginBottom: '1.75rem',
          fontSize: '0.875rem', lineHeight: 1.7
        }}>
          {t('lookingForSomeone')}{' '}
          <span style={{
            color: '#008081', fontWeight: '700',
            backgroundColor: 'rgba(0,128,129,0.1)',
            padding: '0.15rem 0.5rem', borderRadius: '9999px',
            border: '1px solid rgba(0,128,129,0.2)'
          }}>
            {goalCategory || '...'}
          </span>
          {' '}{t('sharesTimezone')}
        </p>

        {/* Your partner ended the match */}
        {partnerEndedMatch && (
          <div style={{
            backgroundColor: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.22)',
            borderRadius: '0.875rem', padding: '0.875rem',
            fontSize: '0.8rem', color: '#f87171',
            marginBottom: '1rem', lineHeight: 1.6
          }}>
            🔓 {t('unmatchMessage', { name: t('yourPartner') })}
          </div>
        )}

        {/* Rematch note */}
        {isRematch && (
          <div style={{
            backgroundColor: 'rgba(192,132,252,0.08)',
            border: '1px solid rgba(192,132,252,0.2)',
            borderRadius: '0.875rem', padding: '0.875rem',
            fontSize: '0.8rem', color: '#c084fc',
            marginBottom: '1rem', lineHeight: 1.6
          }}>
            {t('previousPartnerNote')}
          </div>
        )}

        {/* Status banner */}
        {noMatchFound && attempts > 0 ? (
          <div style={{
            backgroundColor: 'rgba(250,204,21,0.07)',
            border: '1px solid rgba(250,204,21,0.2)',
            borderRadius: '0.875rem', padding: '0.875rem',
            fontSize: '0.8rem', color: '#facc15',
            marginBottom: '1.25rem', lineHeight: 1.6
          }}>
            ⏳ {t('noMatchYet')}
          </div>
        ) : (
          <div style={{
            backgroundColor: 'rgba(0,128,129,0.07)',
            border: '1px solid rgba(0,128,129,0.18)',
            borderRadius: '0.875rem', padding: '0.875rem',
            fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)',
            marginBottom: '1.25rem', lineHeight: 1.6,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <span style={{ color: '#008081', animation: 'pulse 1.5s infinite', display: 'inline-block' }}>●</span>
            {t('autoCheck')}
          </div>
        )}

        {/* Manual check button */}
        <button
          className="waiting-primary-btn"
          onClick={manualCheck}
          disabled={checking || isMatched}
          style={{
            width: '100%', padding: '0.925rem',
            background: checking
              ? 'rgba(0,128,129,0.3)'
              : 'linear-gradient(135deg, #008081, #005f5f)',
            color: '#ffffff', border: 'none',
            borderRadius: '0.875rem', fontWeight: '700',
            cursor: (checking || isMatched) ? 'not-allowed' : 'pointer',
            fontSize: '0.95rem', marginBottom: '0.75rem',
            boxShadow: checking ? 'none' : '0 4px 15px rgba(0,128,129,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}
        >
          {checking ? (
            <>
              <span style={{
                width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#ffffff', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite', display: 'inline-block'
              }} />
              {t('checking')}
            </>
          ) : (
            t('checkNow')
          )}
        </button>

        {/* Community button */}
        {!alreadyInCommunity ? (
          <button
            className="waiting-secondary-btn"
            onClick={handleJoinCommunity}
            disabled={joiningCommunity || !goalCategory}
            style={{
              width: '100%', padding: '0.925rem',
              backgroundColor: 'transparent',
              color: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '0.875rem', fontWeight: '600',
              cursor: (joiningCommunity || !goalCategory) ? 'not-allowed' : 'pointer',
              opacity: (joiningCommunity || !goalCategory) ? 0.6 : 1,
              fontSize: '0.95rem'
            }}
          >
            {joiningCommunity ? t('joining') : t('joinCommunity')}
          </button>
        ) : (
          <button
            className="waiting-secondary-btn"
            onClick={() => setCurrentPage('dashboard')}
            style={{
              width: '100%', padding: '0.925rem',
              backgroundColor: 'transparent',
              color: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '0.875rem', fontWeight: '600',
              cursor: 'pointer', fontSize: '0.95rem'
            }}
          >
            {t('goToCommunity')}
          </button>
        )}

        {/* Manual check count */}
        {attempts > 0 && (
          <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>
            {t('manualChecks').replace('{count}', attempts).replace('{plural}', attempts !== 1 ? 's' : '')}
          </p>
        )}
      </div>

      {/* Searching animation label */}
      <p style={{
        marginTop: '1.5rem', fontSize: '0.8rem',
        color: 'rgba(255,255,255,0.2)', letterSpacing: '0.05em',
        animation: 'fadeInUp 0.6s ease', zIndex: 1
      }}>
        searching{dots}
      </p>
    </div>
  );
}